import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { Favicon } from '@/components/favicon'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { type Competitor, useCompetitors } from '@/lib/competitors'
import { formatCount, formatDate } from '@/lib/format'
import { TableSkeleton, useSiteScope } from '../../-section'
import { AddCompetitorDialog, RemoveCompetitorDialog } from '../-parts'

export const Route = createFileRoute('/_app/$org/$host/competitors/_list/manage')({
  component: ManagePage,
})

/**
 * The set itself: what is being compared, and the two ways to change it.
 *
 * A page rather than a control on the report, because this is the only write in
 * the section and adding one starts a pull — a button that spends parked above
 * a table reads as a filter on the table. Nothing here says what it costs, how
 * often it refreshes or who collects it: that is all `/admin`'s.
 *
 * The counts are what each competitor is actually contributing, which is the
 * only question this page can answer that the nav cannot — the nav says how big
 * their profile is, and here you can see how much of it is not yours, which is
 * the number that decides whether they are worth a slot.
 */
function ManagePage() {
  const { host, site } = useSiteScope()
  const { data, isPending } = useCompetitors(site)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Competitor | null>(null)

  if (isPending || !data) {
    return (
      <>
        <h1 className="sr-only">Manage competitors</h1>
        <TableSkeleton rows={3} />
      </>
    )
  }

  const full = data.competitors.length >= data.limit

  return (
    <>
      <h1 className="sr-only">Manage competitors</h1>

      <Panel
        title="Competitors"
        meta={`${data.competitors.length} of ${data.limit}`}
        flush
        actions={
          !full && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <PlusIcon className="size-3.5" aria-hidden />
              Add
            </Button>
          )
        }
      >
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-border bg-secondary/30">
              <th className="py-2 pr-3 pl-4 text-left">
                <span className="mono-label text-muted-foreground">Domain</span>
              </th>
              <th className="w-[104px] px-3 py-2 text-right">
                <span className="mono-label text-muted-foreground">Domains</span>
              </th>
              <th className="w-[104px] px-3 py-2 text-right">
                <span className="mono-label text-muted-foreground">Not yours</span>
              </th>
              <th className="w-[120px] px-3 py-2 text-right">
                <span className="mono-label text-muted-foreground">Added</span>
              </th>
              <th className="w-12" aria-label="Remove" />
            </tr>
          </thead>

          <tbody>
            {data.competitors.map((competitor) => (
              <tr
                key={competitor.id}
                className="group border-b border-border last:border-b-0 hover:bg-secondary/40"
              >
                <td className="py-2.5 pr-3 pl-4">
                  <span className="flex min-w-0 items-center gap-2">
                    <Favicon host={competitor.host} size={16} className="shrink-0 rounded-[3px]" />
                    <a
                      href={`https://${competitor.host}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="min-w-0 truncate font-mono text-[12.5px] underline-offset-2 hover:underline"
                    >
                      {competitor.host}
                    </a>
                  </span>
                </td>

                <td className="px-3 py-2.5 text-right">
                  <Figure value={competitor.storedDomains} />
                </td>

                <td className="px-3 py-2.5 text-right">
                  <Figure value={competitor.gap} />
                </td>

                <td className="px-3 py-2.5 text-right">
                  {/* When we last read them, not when they were added: the
                      first is what says whether the numbers beside it are the
                      answer or a partial one still arriving. */}
                  <span className="font-mono text-[11.5px] text-muted-foreground tabular">
                    {competitor.lastSyncedAt ? formatDate(competitor.lastSyncedAt) : 'reading'}
                  </span>
                </td>

                <td className="pr-4 text-right">
                  <button
                    type="button"
                    aria-label={`Remove ${competitor.host}`}
                    onClick={() => setRemoving(competitor)}
                    className="cursor-pointer text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/60 hover:!text-destructive focus-visible:text-muted-foreground/60"
                  >
                    <Trash2Icon className="size-3.5" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <AddCompetitorDialog
        open={adding}
        onClose={() => setAdding(false)}
        siteId={site?.id}
        host={host}
      />
      <RemoveCompetitorDialog
        competitor={removing}
        onClose={() => setRemoving(null)}
        siteId={site?.id}
      />
    </>
  )
}

/** A figure we have not collected yet is a dash, not a zero: the difference is
 * whether anybody has looked. */
function Figure({ value }: { value: number | null }) {
  return (
    <span className="font-mono text-[12px] tabular">
      {value === null ? <span className="text-muted-foreground/50">—</span> : formatCount(value)}
    </span>
  )
}
